import { useState, useEffect, useRef } from 'react';
import { api, API_BASE } from '../../utils/api';
import { Button, Toggle, useToast, Card, Input } from '../shared/UI';

export function SystemSettings() {
  const [config, setConfig] = useState({});
  const [orgName, setOrgName] = useState('');
  const [ticketTemplate, setTicketTemplate] = useState('');
  const [maxCalls, setMaxCalls] = useState(3);
  
  // Kiosk Customization
  const [welcomeText, setWelcomeText] = useState('');
  const [bgColor, setBgColor] = useState('linear-gradient(135deg, #0d1b2a 0%, #1b263b 100%)');
  const [textColor, setTextColor] = useState('#ffffff');
  const [tickerMessage, setTickerMessage] = useState('');
  const [logoPath, setLogoPath] = useState('');
  const [fontSize, setFontSize] = useState('52px');
  const [fontFamily, setFontFamily] = useState("'Outfit', sans-serif");

  const logoInputRef = useRef();
  const { show, ToastContainer } = useToast();

  const loadConfig = async () => {
    const cfg = await api.get('/api/admin/config');
    setConfig(cfg);
    setOrgName(cfg.org_name || '');
    setTicketTemplate(cfg.ticket_template || '');
    setMaxCalls(parseInt(cfg.max_call_count) || 3);
    setWelcomeText(cfg.kiosk_welcome_text || '');
    setBgColor(cfg.kiosk_bg_color || 'linear-gradient(135deg, #0d1b2a 0%, #1b263b 100%)');
    setTextColor(cfg.kiosk_text_color || '#ffffff');
    setTickerMessage(cfg.kiosk_ticker_message || '');
    setLogoPath(cfg.kiosk_logo_path || '');
    setFontSize(cfg.kiosk_font_size || '52px');
    setFontFamily(cfg.kiosk_font_family || "'Outfit', sans-serif");
  };

  useEffect(() => { loadConfig(); }, []);

  const save = async () => {
    try {
      await api.post('/api/admin/config', {
        ...config,
        org_name: orgName,
        ticket_template: ticketTemplate,
        max_call_count: String(maxCalls),
        kiosk_welcome_text: welcomeText,
        kiosk_bg_color: bgColor,
        kiosk_text_color: textColor,
        kiosk_ticker_message: tickerMessage,
        kiosk_logo_path: logoPath,
        kiosk_font_size: fontSize,
        kiosk_font_family: fontFamily,
      });
      show('Settings updated successfully');
    } catch (e) {
      show('Failed to save settings', 'error');
    }
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const formData = new FormData();
    formData.append('file', file);
    try {
      const res = await api.upload('/api/media/upload', formData);
      setLogoPath(res.file_path);
      show('Logo uploaded');
    } catch (err) {
      show('Upload failed', 'error');
    }
  };

  const toggle = (key) => {
    const current = config[key] === 'true';
    setConfig(c => ({ ...c, [key]: (!current).toString() }));
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 28, maxWidth: 800 }}>
      <ToastContainer />
      <div>
        <h1 style={{ fontFamily: 'var(--font-display)', fontWeight: 800, fontSize: 28, color: 'var(--navy)' }}>System Settings</h1>
        <p style={{ color: 'var(--gray-500)', marginTop: 4, fontSize: 14 }}>Global branding, shop behavior, and ticket customization</p>
      </div>

      {/* Branding */}
      <Card>
        <div style={{ padding: 28 }}>
          <h2 style={{ fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 16, color: 'var(--navy)', marginBottom: 20 }}>Branding & Identity</h2>
          <Input label="Organization Name" value={orgName} onChange={setOrgName} placeholder="e.g. Acme Hospital" />
        </div>
      </Card>

      {/* Kiosk Appearance */}
      <Card>
        <div style={{ padding: 28, display: 'flex', flexDirection: 'column', gap: 24 }}>
          <h2 style={{ fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 16, color: 'var(--navy)', marginBottom: -8 }}>Shop Appearance</h2>
          
          <div style={{ display: 'flex', gap: 24, alignItems: 'center' }}>
            <div style={{ width: 100, height: 100, borderRadius: 16, background: 'var(--gray-50)', border: '2px dashed var(--gray-200)', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}>
              {logoPath ? <img src={`${API_BASE}${logoPath}`} style={{ width: '100%', height: '100%', objectFit: 'contain' }} alt="Logo" /> : <span style={{ fontSize: 24 }}>🖼</span>}
            </div>
            <div>
              <input type="file" ref={logoInputRef} hidden onChange={handleLogoUpload} accept="image/*" />
              <Button variant="ghost" size="sm" onClick={() => logoInputRef.current.click()}>Change Shop Logo</Button>
              <div style={{ fontSize: 12, color: 'var(--gray-500)', marginTop: 6 }}>Appears above the welcome text</div>
            </div>
          </div>

          <div>
            <Input label="Welcome Headline" value={welcomeText} onChange={setWelcomeText} placeholder="e.g. Welcome to {org_name}" />
            <div style={{ fontSize: 11, color: 'var(--cobalt)', marginTop: 6, padding: '4px 8px', background: 'var(--ice)', borderRadius: 6, display: 'inline-block' }}>
              Tip: Use <code>&lt;span style="color: yellow"&gt;Text&lt;/span&gt;</code> for multi-color headlines.
            </div>
          </div>
          
          <div className="resp-grid-2" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              <label style={{ fontSize: 13, fontWeight: 600, color: 'var(--gray-700)' }}>Background Style</label>
              <input value={bgColor} onChange={e => setBgColor(e.target.value)} 
                placeholder="CSS Color or Gradient"
                style={{ width: '100%', padding: '12px 14px', borderRadius: 10, border: '1.5px solid var(--gray-100)', outline: 'none' }} />
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              <label style={{ fontSize: 13, fontWeight: 600, color: 'var(--gray-700)' }}>Base Text Color</label>
              <div style={{ display: 'flex', gap: 10 }}>
                <input type="color" value={textColor} onChange={e => setTextColor(e.target.value)} style={{ width: 44, height: 44, border: 'none', background: 'none', cursor: 'pointer' }} />
                <input value={textColor} onChange={e => setTextColor(e.target.value)} 
                  style={{ flex: 1, padding: '12px 14px', borderRadius: 10, border: '1.5px solid var(--gray-100)', outline: 'none' }} />
              </div>
            </div>
          </div>

          <div className="resp-grid-2" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              <label style={{ fontSize: 13, fontWeight: 600, color: 'var(--gray-700)' }}>Font Family</label>
              <select value={fontFamily} onChange={e => setFontFamily(e.target.value)}
                style={{ width: '100%', padding: '12px 14px', borderRadius: 10, border: '1.5px solid var(--gray-100)', outline: 'none' }}>
                <option value="'Outfit', sans-serif">Outfit (Modern/Clean)</option>
                <option value="'Inter', sans-serif">Inter (Professional)</option>
                <option value="'Roboto', sans-serif">Roboto (Standard)</option>
                <option value="'Playfair Display', serif">Playfair (Elegant)</option>
                <option value="'Courier New', monospace">Monospace (Technical)</option>
              </select>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              <label style={{ fontSize: 13, fontWeight: 600, color: 'var(--gray-700)' }}>Font Size (Headline)</label>
              <Input value={fontSize} onChange={setFontSize} placeholder="e.g. 52px" />
            </div>
          </div>

          <Input label="News Ticker / Special Message" value={tickerMessage} onChange={setTickerMessage} placeholder="e.g. We are now accepting M-Pesa payments at Booth 4" />
        </div>
      </Card>

      {/* Kiosk Behavior */}
      <Card>
        <div style={{ padding: 28, display: 'flex', flexDirection: 'column', gap: 24 }}>
          <h2 style={{ fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 16, color: 'var(--navy)', marginBottom: -8 }}>Shop Behavior</h2>

          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '18px 20px', background: config.data_collection_enabled === 'true' ? 'var(--ice)' : 'var(--gray-50)', borderRadius: 12, border: '1px solid var(--gray-100)' }}>
            <div>
              <div style={{ fontWeight: 600, fontSize: 15, color: 'var(--navy)' }}>Customer Data Collection</div>
              <div style={{ fontSize: 13, color: 'var(--gray-500)', marginTop: 3 }}>
                Ask customers for their full name and phone number (with country code selector).
              </div>
            </div>
            <Toggle
              checked={config.data_collection_enabled === 'true'}
              onChange={() => toggle('data_collection_enabled')} />
          </div>

          <div style={{ padding: '18px 20px', background: 'var(--gray-50)', borderRadius: 12, border: '1px solid var(--gray-100)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16 }}>
              <div>
                <div style={{ fontWeight: 600, fontSize: 15, color: 'var(--navy)' }}>Max Calls Per Ticket</div>
                <div style={{ fontSize: 13, color: 'var(--gray-500)', marginTop: 3 }}>Number of re-calls before skipping.</div>
              </div>
              <div style={{ fontFamily: 'var(--font-display)', fontWeight: 800, fontSize: 32, color: 'var(--cobalt)', minWidth: 48, textAlign: 'center' }}>{maxCalls}</div>
            </div>
            <input type="range" min={1} max={10} value={maxCalls} onChange={e => setMaxCalls(parseInt(e.target.value))}
              style={{ width: '100%', accentColor: 'var(--cobalt)', height: 6 }} />
          </div>
        </div>
      </Card>

      {/* Ticket Customization */}
      <Card>
        <div style={{ padding: 28 }}>
          <h2 style={{ fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 16, color: 'var(--navy)', marginBottom: 16 }}>Ticket Print Layout</h2>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <label style={{ fontSize: 13, fontWeight: 600, color: 'var(--gray-600)' }}>Layout Template</label>
            <textarea 
              value={ticketTemplate} 
              onChange={e => setTicketTemplate(e.target.value)}
              style={{ width: '100%', minHeight: 120, padding: 14, borderRadius: 10, border: '1.5px solid var(--gray-100)', fontFamily: 'var(--font-mono)', fontSize: 13, background: 'var(--gray-50)', resize: 'vertical' }}
            />
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
              {['{org_name}', '{ticket}', '{service}', '{counter}', '{date}', '{customer}'].map(tag => (
                <span key={tag} style={{ fontSize: 11, background: 'var(--ice)', color: 'var(--cobalt)', padding: '2px 8px', borderRadius: 4, cursor: 'pointer' }} onClick={() => setTicketTemplate(t => t + tag)}>{tag}</span>
              ))}
            </div>
          </div>
        </div>
      </Card>

      <Button onClick={save} size="lg" style={{ alignSelf: 'flex-start' }}>💾 Save All Settings</Button>
    </div>
  );
}
